//== state container for the FHS service charges calculator
//== factory pattern: each createStore() call returns an isolated store
//== no module-level state; safe to instantiate multiple times in tests
//==
//== deliberately smaller than the redemption calculator's store: this is a
//== single-screen calculator, so there are no steps, no validSteps set, and
//== no derived recompute pass. results are computed at render time from
//== calculate.js rather than cached in state.

export function createInitialState() {
  return {
    inputs: {
      purchasePrice: null,
      originalEquity: null,
      currentEquityPct: null,
    },
    //== which fields the customer has actually left, per field. errors are
    //== gated on this so a blank form doesn't open covered in red - the same
    //== job armed.js does per step for the redemption calculator, done per
    //== field here because there is no Continue button to arm on.
    touched: {},
  }
}

//== createStore({ config })
//== - config: from window.calculatorData.serviceCharges (rates, band labels, copy)
export function createStore({ config } = {}) {
  let state = createInitialState()
  const subscribers = new Set()

  function notify() {
    for (const fn of subscribers) fn(state)
  }

  return {
    getState: () => state,

    config: config ?? {},

    setState(patch) {
      if (!patch || typeof patch !== 'object') return
      state = applyPatch(state, patch)
      notify()
    },

    //== mark a field as left-by-the-customer, so its error may now show
    touch(field) {
      if (!field || state.touched[field]) return
      state = { ...state, touched: { ...state.touched, [field]: true } }
      notify()
    },

    subscribe(fn) {
      subscribers.add(fn)
      return () => subscribers.delete(fn)
    },

    reset() {
      state = createInitialState()
      notify()
    },
  }
}

//== shallow-merges top-level keys; deep-merges the known nested objects
function applyPatch(prev, patch) {
  const next = { ...prev, ...patch }
  if (patch.inputs) next.inputs = { ...prev.inputs, ...patch.inputs }
  if (patch.touched) next.touched = { ...prev.touched, ...patch.touched }
  return next
}
